import { useMemo } from 'react';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { config } from '../../../config/config';
import { readM24Error } from '../../music24/utils/parseM24LimitError';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';

interface UseCustomerPortalUrlOptions {
  enabled?: boolean;
}

export const useCustomerPortalUrl = (options?: UseCustomerPortalUrlOptions) => {
  const trpcAPI = useTRPCAPI();
  // App Store 3.1.1 / Play billing: a native app's web view never opens the Stripe portal, and
  // never falls back to the Stripe billing login either. The app sells and manages the plan.
  const isNativeEmbed = useIsNativeEmbed();
  const enabled = (options?.enabled ?? true) && !isNativeEmbed;

  const { data, isLoading, error } = useTRPCQuery(
    trpcAPI.billing.createCustomerPortalSession.queryOptions(undefined, {
      enabled,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })
  );

  const portalUrl = useMemo(() => {
    if (isNativeEmbed) {
      return null;
    }
    if (error) {
      // A member never has a billing customer to open a portal for — no fallback URL either, so
      // the caller (ErrorView) must gate this on the role, not just on `portalUrl` being null.
      if (readM24Error(error)?.code === 'M24_TEAM_OWNER_ONLY') {
        return null;
      }
      return config.stripeBillingUrl;
    }
    return data?.url ?? null;
  }, [data?.url, error, isNativeEmbed]);

  return {
    portalUrl,
    isLoading,
    error,
  };
};
